import type {
  AppAccountAnswer,
  AppAccountRef,
} from '@/features/patients/types';
import {
  appAccountClearedText,
  appAccountTime,
  appLinkMethodLabel,
} from '@/pages/patients/app-account-format';
import styles from './AppAccountConfirm.module.css';

/**
 * RL-1118 (security review S1): before counter eKYC can verify a record that
 * has an app account, the counter asks the patient whether that app account
 * is theirs. The API takes `appAccount: confirmed | not_theirs` on
 * POST /patients/:id/ekyc and refuses without it (422
 * APP_ACCOUNT_CONFIRMATION_REQUIRED).
 */
export function AppAccountConfirm({
  account,
  answer,
  onAnswer,
  disabled,
}: {
  account: AppAccountRef;
  answer: AppAccountAnswer | null;
  onAnswer: (answer: AppAccountAnswer) => void;
  disabled?: boolean;
}) {
  return (
    <fieldset className={styles.box} disabled={disabled}>
      <legend className={styles.legend}>
        Is this app account the patient&apos;s?
      </legend>
      <p className={styles.account}>
        App account phone{' '}
        <span className="mono">{account.phone ?? 'not shown'}</span>
        {account.method && <> · {appLinkMethodLabel(account.method)}</>}
        {account.linkedAt && <> · linked {appAccountTime(account.linkedAt)}</>}
      </p>
      <p className={styles.hint}>
        Ask the patient to show the phone with the app, or read out the last
        digits. Choose an answer to continue.
      </p>
      <label className={styles.option}>
        <input
          type="radio"
          name="app-account-answer"
          value="confirmed"
          checked={answer === 'confirmed'}
          onChange={() => onAnswer('confirmed')}
        />
        <span>
          <strong>Yes, it is theirs</strong>
          <span className={styles.hint}>
            Keeps the link. The app can show this record once eKYC is marked
            verified.
          </span>
        </span>
      </label>
      <label className={styles.option}>
        <input
          type="radio"
          name="app-account-answer"
          value="not_theirs"
          checked={answer === 'not_theirs'}
          onChange={() => onAnswer('not_theirs')}
        />
        <span>
          <strong>No, it is not theirs</strong>
          <span className={styles.hint}>
            Unlinks the app account from this record.
          </span>
        </span>
      </label>
      {answer === 'not_theirs' && (
        <div
          className={styles.notTheirs}
          role="note"
          aria-label="What not theirs does"
        >
          <strong>When you mark verified with “not theirs”:</strong>
          <ul>
            <li>
              The app account is unlinked from this record and signed out of the
              app; whoever holds that phone can no longer see this record.
            </li>
            <li>
              Share links and codes that app account made for this record stop
              working.
            </li>
            <li>{appAccountClearedText(account.method)}</li>
            <li>
              The record itself stays and is marked eKYC-verified for this
              patient. The change is audited.
            </li>
          </ul>
        </div>
      )}
    </fieldset>
  );
}
